import React, { useContext, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { CardActionArea, CardActions, IconButton } from "@mui/material";
import { defaultImageUrl } from "../../config_and_helpers/config";
import ProductDetailModal from "./ProductDetailModal";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { showToast } from "../../config_and_helpers/helpers";
import { AppContext, CartAppContext } from "../../App";
import { useIsLabViewer } from "../../hooks/useIsLabViewer";
import { getSupplierDetails } from "../../clients/supplier_client";
import {
  formatUnitQuantity,
  getProductCatalogNumber,
  getProductImageUrl,
  getProductInventoryId,
  getProductSupplierId,
} from "../../utils/productDisplay";

/**
 * Represents an item card component, displaying key details of a product and providing quick actions.
 *
 * This component shows a product's information like its name, catalogue number, supplier, manufacturer, category,
 * and unit quantity. It allows users to view more details about the product and to add the product to a shopping cart.
 * The card uses Material UI components for a consistent and responsive design.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.product - The product object containing information to display on the card.
 * @param {Function} props.handleEdit - Callback function to handle updates to the product.
 * @returns The ItemCard component.
 *
 * Usage:
 * ```jsx
 * <ItemCard
 *    product={productDetails}
 *    handleEdit={editHandler}
 * />
 * ```
 */
const ItemCard = ({ product, handleEdit, isCatalogueView = false }) => {
  // Context for managing cart items.
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const { cart, setCart } = useContext(CartAppContext);

  // State for controlling the visibility of the product detail modal.
  const [show, setShow] = useState(false);

  const [supplier, setSupplier] = useState();

  useEffect(() => {
    const sid = getProductSupplierId(product);
    if (product && sid) {
      getSupplierDetails(token, sid, setSupplier);
    }
  }, [product, token]);

  const imageUrl = getProductImageUrl(product, defaultImageUrl);
  const catalogNumber = getProductCatalogNumber(product);
  const supplierId = getProductSupplierId(product);
  const canAddToCart = Boolean(supplierId && (supplier?.id || product.supplier_name));

  const resolveSupplierForCart = () => {
    if (supplier?.id) {
      return { id: supplier.id, name: supplier.name };
    }
    if (supplierId && product.supplier_name) {
      return { id: supplierId, name: product.supplier_name };
    }
    return null;
  };

  const handleQuickAddToCart = () => {
    const supplierForCart = resolveSupplierForCart();
    if (!supplierForCart) {
      showToast("Supplier details are still loading. Try again.", "warning", "bottom-right", 2000);
      return;
    }

    const itemExists = cart.some((item) => item.cat_num === catalogNumber);

    // Update the cart based on whether the product is already present.
    if (itemExists) {
      // If the product is in the cart, increase its quantity.
      setCart((prevCart) =>
        prevCart.map((item) =>
          item.cat_num === catalogNumber
            ? { ...item, quantity: Number(item.quantity) + Number(1) }
            : item,
        ),
      );
    } else {
      const newItem = {
        product: getProductInventoryId(product),
        cat_num: catalogNumber,
        name: product.name,
        image_url: getProductImageUrl(product, null),
        supplier: supplierForCart,
        quantity: Number(1),
      };
      setCart((prevCart) => [...prevCart, newItem]);
    }
    // Show a toast notification for adding to the cart.
    showToast("Product added to cart", "success", "bottom-right", 1500);
  };

  return (
    <>
      {/* Material UI Card component containing product details. */}
      <Card sx={{ maxWidth: 400, maxHeight: 500, mx: 'auto' }}>
        {/* Area for user interaction, opens the detail modal on click. */}
        <CardActionArea onClick={() => setShow(true)}>
          {/* Product image. */}
          <CardMedia component="img" height="250" image={imageUrl} />
          {/* Content section with product details. */}
          <CardContent>
            {/* Display product name, catalogue number, and other attributes. */}
            <Typography gutterBottom variant="h4" component="div">
              {product.name}
            </Typography>
            <Typography gutterBottom variant="subtitle1" component="div">
              {catalogNumber}
            </Typography>
            <Typography variant="body1" color="text.secondary" component="div">
              <div
                style={{
                  display: "flex",
                  justifyContent: "start",
                  alignItems: "center",
                }}
              >
                <div>
                  <span style={{ fontWeight: "bold" }}>Supplier:</span>{" "}
                  {supplier?.name || product.supplier_name || "—"}
                </div>
              </div>
              <div style={{ marginTop: "6px" }}>
                <span style={{ fontWeight: "bold" }}>Category:</span>{" "}
                {product.category}
              </div>
              <div style={{ marginTop: "6px" }}>
                <span style={{ fontWeight: "bold" }}>Unit Quantity:</span>{" "}
                {formatUnitQuantity(product.unit_quantity) || "—"}
                {product.unit ? ` (${product.unit})` : ""}
              </div>
            </Typography>
          </CardContent>
        </CardActionArea>
        {/* Action area for the card, with a button to add the product to the cart. */}
        <CardActions>
          {!isViewer ? (
          <IconButton
            aria-label="add to cart"
            sx={{ color: "primary.main" }}
            onClick={handleQuickAddToCart}
            disabled={!canAddToCart}
          >
            <AddShoppingCartIcon />
          </IconButton>
          ) : null}
        </CardActions>
      </Card>

      {/* Modal for displaying detailed product information. */}
      <ProductDetailModal
        productObj={product}
        shopView={true}
        isCatalogueView={isCatalogueView}
        showShopModal={show}
        setShowShopModal={setShow}
        updateProducts={handleEdit}
      />
    </>
  );
};
export default ItemCard;
